import { useMemo, useState } from 'react'
import { money } from '../lib/money'

/**
 * Đếm tiền trong két theo mệnh giá.
 *
 * Không bắt người bán tự cộng nhẩm rồi gõ một con số — đó là chỗ sai nhiều nhất và
 * sai rồi thì không ai dò lại được. Đếm theo từng loại tờ giống hệt cách họ chia tiền
 * ra thành từng cọc trên bàn: 20 tờ 50.000, 7 tờ 100.000… App tự cộng.
 *
 * Bỏ tiền xu — gần như không còn lưu hành.
 */
const NOTES = [500_000, 200_000, 100_000, 50_000, 20_000, 10_000, 5_000, 2_000, 1_000] as const

interface Props {
  value: number
  onChange: (total: number) => void
}

export function CashCounter({ onChange }: Props) {
  const [counts, setCounts] = useState<Record<number, number>>({})

  const total = useMemo(
    () => NOTES.reduce((sum, note) => sum + note * (counts[note] ?? 0), 0),
    [counts],
  )

  function bump(note: number, delta: number) {
    setCounts((c) => {
      const next = { ...c, [note]: Math.max(0, (c[note] ?? 0) + delta) }
      onChange(NOTES.reduce((sum, n) => sum + n * (next[n] ?? 0), 0))
      return next
    })
  }

  function setCount(note: number, raw: string) {
    const n = Math.max(0, Math.min(9999, Number(raw.replace(/\D/g, '')) || 0))
    setCounts((c) => {
      const next = { ...c, [note]: n }
      onChange(NOTES.reduce((k, m) => k + m * (next[m] ?? 0), 0))
      return next
    })
  }

  return (
    <div className="counter">
      {NOTES.map((note) => {
        const n = counts[note] ?? 0
        return (
          <div key={note} className={`counter-row${n > 0 ? ' has' : ''}`}>
            <span className="counter-note num">{money(note)}</span>

            <div className="stepper">
              <button
                type="button"
                onClick={() => bump(note, -1)}
                disabled={n === 0}
                aria-label={`Bớt tờ ${money(note)}`}
              >
                −
              </button>
              <input
                className="num"
                inputMode="numeric"
                value={n === 0 ? '' : n}
                placeholder="0"
                onChange={(e) => setCount(note, e.target.value)}
                aria-label={`Số tờ ${money(note)}`}
              />
              <button type="button" onClick={() => bump(note, 1)} aria-label={`Thêm tờ ${money(note)}`}>
                +
              </button>
            </div>

            <span className="counter-sub num">{n > 0 ? money(note * n) : ''}</span>
          </div>
        )
      })}

      <div className="counter-total">
        <span>Đếm được</span>
        <b className="num">{money(total)}</b>
      </div>
    </div>
  )
}
